import { defineStore } from 'pinia'
import { ref } from 'vue'

import { http } from '@/api/http'

import { useAuthStore } from './auth'

/** Badge counts from `GET main/counts` (wp/shell §5.6). */
export type CountKey =
  | 'chatUnread'
  | 'messagesCount'
  | 'trashCount'
  | 'documentsExpiring'
  | 'notesDue'
  | 'leavesCount'
  | 'timesheetsCount'
  | 'notPaidIncomes'
  | 'notPaidExpenses'

/** A count key, or the Finances group badge (unpaid incomes + unpaid expenses, legacy `FinGlobal`). */
export type BadgeKey = CountKey | 'finGlobal'

export type CountsAnswer = Partial<Record<CountKey, number>>

/** Legacy polled every 10 s (`AppNavbarVerticalLayout.vue:464-468`). */
export const COUNTS_INTERVAL = 10_000

/**
 * `chat/unreadByWorkspace` (the switcher's badges) is polled less: unlike `main/counts` it counts against the API's
 * rate limit, and the chat's live events from the other workspaces refresh it at once (CallRinger).
 */
export const WORKSPACES_INTERVAL = 60_000

const CHAT_USERS = ['Client', 'Staff', 'Freelancer']

export interface WorkspaceUnread {
  workspace_id: number
  unread: number
}

const KEYS: CountKey[] = [
  // The chat's unread total (the Messages badge); `messagesCount` is the old inbox's, kept for the mobile app.
  'chatUnread',
  'messagesCount',
  'trashCount',
  // BR-095: the documents in their reminder window or ended (the Documents badge and the navbar's alerts)
  'documentsExpiring',
  // Remind me: my reminders on notes due today or earlier (the Notes badge)
  'notesDue',
  'leavesCount',
  'timesheetsCount',
  'notPaidIncomes',
  'notPaidExpenses',
]

/**
 * The badge store (KIT_REQUIREMENTS.md §2). The shell polls `main/counts` while a vertical layout is mounted and stops
 * on logout (NEW-14); modules call `refresh()` after an action that changes a badge (trash, leave, timesheet, invoice
 * — FX-029) instead of waiting for the next poll. Every numeric count is stored, zero included (FX-060: legacy kept a
 * badge that dropped to 0).
 */
export const useCountsStore = defineStore('counts', () => {
  const counts = ref<Partial<Record<CountKey, number>>>({})
  const polling = ref(false)
  /** Unread chat messages per workspace id, for a person on several (the current one included; 0s left out). */
  const workspaceUnread = ref<Record<number, number>>({})
  let timer: ReturnType<typeof setInterval> | null = null
  let workspacesTimer: ReturnType<typeof setInterval> | null = null
  let inFlight: Promise<void> | null = null
  let workspacesInFlight: Promise<void> | null = null

  async function load(): Promise<void> {
    const auth = useAuthStore()
    if (!auth.isLoggedIn) return
    try {
      // The shared client: Auth-Key, the workspace/software defaults and the 401 handler (legacy used raw axios).
      const { data } = await http.get<CountsAnswer>('main/counts')
      const next: Partial<Record<CountKey, number>> = {}
      for (const key of KEYS) {
        const value = data[key]
        if (typeof value === 'number') next[key] = value
      }
      counts.value = next
    } catch {
      // A 401 ends the session through the handler; any other failure keeps the last counts until the next poll.
    }
  }

  async function loadWorkspaces(): Promise<void> {
    const auth = useAuthStore()
    // One workspace: nothing to show elsewhere, and no request.
    if (!auth.isLoggedIn || !CHAT_USERS.includes(auth.userType ?? '') || (auth.session?.workspaces.length ?? 0) < 2) {
      workspaceUnread.value = {}
      return
    }
    try {
      const { data } = await http.get<WorkspaceUnread[]>('chat/unreadByWorkspace')
      const next: Record<number, number> = {}
      for (const entry of Array.isArray(data) ? data : []) {
        if (typeof entry?.unread === 'number' && entry.unread > 0) next[Number(entry.workspace_id)] = entry.unread
      }
      workspaceUnread.value = next
    } catch {
      // A host without the endpoint, or a failure: the last badges stay until the next read.
    }
  }

  /** Re-reads the per-workspace unread now (deduplicated while a read is in flight). */
  function refreshWorkspaces(): Promise<void> {
    workspacesInFlight ??= loadWorkspaces().finally(() => (workspacesInFlight = null))
    return workspacesInFlight
  }

  /** Re-reads the counts now (deduplicated while a read is in flight). */
  function refresh(): Promise<void> {
    inFlight ??= load().finally(() => (inFlight = null))
    return inFlight
  }

  function start(interval = COUNTS_INTERVAL): void {
    stop()
    polling.value = true
    void refresh()
    timer = setInterval(() => void refresh(), interval)
    void refreshWorkspaces()
    workspacesTimer = setInterval(() => void refreshWorkspaces(), WORKSPACES_INTERVAL)
  }

  function stop(): void {
    if (timer) clearInterval(timer)
    if (workspacesTimer) clearInterval(workspacesTimer)
    timer = null
    workspacesTimer = null
    polling.value = false
  }

  /** Logout: stop polling and forget the counts. */
  function reset(): void {
    stop()
    counts.value = {}
    workspaceUnread.value = {}
  }

  function badge(key: BadgeKey): number {
    if (key === 'finGlobal') return (counts.value.notPaidIncomes ?? 0) + (counts.value.notPaidExpenses ?? 0)
    return counts.value[key] ?? 0
  }

  /** Unread chat messages in workspace `id` (0 when none, or with a single workspace). */
  function workspaceBadge(id: number | string | null | undefined): number {
    return workspaceUnread.value[Number(id)] ?? 0
  }

  return { counts, polling, workspaceUnread, refresh, refreshWorkspaces, start, stop, reset, badge, workspaceBadge }
})
